/**
 * The mute sheet: exactly the six durations, plus `Unmute` when already
 * muted, through `notification-api`.
 *
 * Both mutations pass their response straight to `onMuteChanged`, AND
 * invalidate messaging-api's `/chat/get` and `/chat/list-mine`.
 *
 * THAT SECOND HALF IS NOT REDUNDANT, and leaving it out was a real bug found
 * by ISSUE-281's independent QA. Mute lives in two places: this dialog's
 * caller keeps a local copy, but the conversation HEADER reads
 * `chatGet.data.muteState`. `useChatMuteSet` invalidates only
 * notification-api's own `chatMuteGet`, so without the line below a fan mutes,
 * closes the panel, and the header still shows no muted line until an
 * incidental refetch. ISSUE-1055 added `/chat/list-mine` for the same
 * reason: the Messages list row shows Muted and hides the unread count from
 * that query.
 *
 * The plan said this dialog "never refetches /chat/get" while another of its
 * work items said the header reads /chat/get. Both cannot be true.
 */
import {
  useChatMuteClear,
  useChatMuteSet,
} from "@sportsmet/notification-sdk/private";
import { Dialog } from "@sportsmet/ui";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { showApiError } from "../../errors/apiErrorBus";
import { MUTE_DURATIONS, type typeChatMuteDuration } from "./muteDisplay";

/**
 * The header reads muteState off /chat/get and the Messages list reads
 * isMuted off /chat/list-mine, so both queries are told. Shared with the
 * Messages list row's own Unmute (ISSUE-1362).
 *
 * Invalidated by the two-segment prefix, which is the shape the generated
 * hooks use themselves (see notification-sdk's own useChatMuteSet). It
 * matches every page and filter of each query, so a stale header or row
 * cannot survive on a key this call site guessed wrong.
 */
export async function refreshChatMuteReads(
  queryClient: QueryClient,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: ["post:chat:chatGet", "svc:messaging-api"],
    }),
    queryClient.invalidateQueries({
      queryKey: ["post:chat:chatListMine", "svc:messaging-api"],
    }),
  ]);
}

export type typeMuteDialogProps = {
  chatId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isMuted: boolean;
  onMuteChanged: (next: {
    muteState: "off" | "timed" | "indefinite";
    mutedUntil: string | null;
  }) => void;
};

export const MuteDialog = (props: typeMuteDialogProps): ReactNode => {
  const chatMuteSet = useChatMuteSet();
  const chatMuteClear = useChatMuteClear();
  const queryClient = useQueryClient();

  const refreshMessagingReads = (): Promise<void> =>
    refreshChatMuteReads(queryClient);

  const setDuration = async (duration: typeChatMuteDuration): Promise<void> => {
    try {
      const result = await chatMuteSet.mutateAsync({
        chatId: props.chatId,
        duration,
      });
      const data = result.data ?? null;
      if ((result.error ?? null) !== null || data === null) {
        showApiError(result);
        return;
      }
      props.onMuteChanged({
        muteState: data.muteState,
        mutedUntil: data.mutedUntil ?? null,
      });
      await refreshMessagingReads();
      props.onOpenChange(false);
    } catch (error) {
      showApiError(error);
    }
  };

  const unmute = async (): Promise<void> => {
    try {
      const result = await chatMuteClear.mutateAsync({ chatId: props.chatId });
      const data = result.data ?? null;
      if ((result.error ?? null) !== null || data === null) {
        showApiError(result);
        return;
      }
      props.onMuteChanged({
        muteState: data.muteState,
        mutedUntil: null,
      });
      await refreshMessagingReads();
      props.onOpenChange(false);
    } catch (error) {
      showApiError(error);
    }
  };

  return (
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      title="Mute this chat"
      description="Every message pings until you say otherwise. Muting quiets the pings, new messages still land here."
      autoFocus={false}
    >
      <ul className="flex flex-col">
        {MUTE_DURATIONS.map((duration) => (
          <li key={duration.value}>
            <button
              type="button"
              data-testid={`mute-duration-${duration.value}`}
              className="flex min-h-touch-target w-full items-center text-left text-body text-ink"
              onClick={() => {
                void setDuration(duration.value);
              }}
            >
              {duration.label}
            </button>
          </li>
        ))}
        {props.isMuted ? (
          <li>
            <button
              type="button"
              data-testid="mute-unmute"
              className="flex min-h-touch-target w-full items-center text-left text-body-strong text-ink"
              onClick={() => {
                void unmute();
              }}
            >
              Unmute
            </button>
          </li>
        ) : null}
      </ul>
    </Dialog>
  );
};
